// src/components/Admin/Category/PricingRuleItem.jsx
import React from 'react';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../../ui/card'; // [cite: 15]
import { Badge } from '../../../ui/badge'; // [cite: 18]
import { cn } from '../../../../lib/utils';

export default function PricingRuleItem({ rule, onEdit, onDelete, canEdit, questionList }) {

    const getConditionSummary = (conditionDefinition, config, localQuestionList) => {
        if (!conditionDefinition || !config) return "Invalid condition configuration";

        const code = conditionDefinition.condition_code;
        let summary = `${conditionDefinition.name}: `;

        try {
            if ((code === 'Q_MULTI_OPTION_ALL_MATCH' || code === 'Q_SINGLE_OPTION_MATCH') && config.category_question_id) {
                const questionId = parseInt(config.category_question_id, 10);
                const question = (localQuestionList || []).find(q => q.id === questionId);
                const questionLabel = question ? (question.label || question.value || `ID ${questionId}`) : `Question ID ${questionId}`;
                summary += `${questionLabel}`;

                if (code === 'Q_SINGLE_OPTION_MATCH' && config.required_option_id && question?.options) {
                    const optionId = parseInt(config.required_option_id, 10);
                    const option = question.options.find(o => o.id === optionId);
                    summary += ` -> Option: ${option ? (option.label || option.value || `ID ${optionId}`) : `ID ${optionId}`}`;
                } else if (code === 'Q_MULTI_OPTION_ALL_MATCH' && Array.isArray(config.required_option_ids) && config.required_option_ids.length > 0 && question?.options) {
                    const optionLabels = config.required_option_ids.map(optId => {
                        const option = question.options.find(o => o.id === parseInt(optId, 10));
                        return option ? (option.label || option.value || `ID ${optId}`) : `ID ${optId}`;
                    }).join(' & ');
                    summary += ` -> Options: [${optionLabels}]`;
                } else if (code === 'Q_MULTI_OPTION_ALL_MATCH') {
                    summary += ` -> Options: [${JSON.stringify(config.required_option_ids || [])}]`;
                }
            } else if (code.startsWith('LOC_') && config.value_to_match) {
                summary += `Match '${config.value_to_match}'`;
            } else if (code === 'EMAIL_DOMAIN_TYPE_MATCH' && config.expected_domain_type) {
                summary += `Expected type '${config.expected_domain_type}'`;
            } else if (code === 'ATTR_FIELD_MATCH' && config.attribution_field_name) {
                summary += `Field '${config.attribution_field_name}' matches '${config.value_to_match || 'any'}'`;
            } else if (code.startsWith('LEAD_AGE_') && config.hours !== undefined) {
                summary += `Hours ${config.operator || '>'} ${config.hours}`;
            } else {
                const configSummary = Object.entries(config)
                    .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
                    .join(', ');
                summary += configSummary || "No specific parameters";
            }
        } catch (e) {
            console.error("Error parsing condition config for summary:", e, config);
            summary += "Error displaying config details.";
        }
        return summary;
    };
    
    const modifierActionDisplay = (rule?.modifier_value_absolute || rule.modifier_value_percentage_rate) > 0 ? 'INCREASE ' : 'DECREASE ';
    const modifierValueDisplay = rule.modifier_type === 'ABSOLUTE'
        ? `${modifierActionDisplay}${rule.modifier_value_absolute !== null ? rule.modifier_value_absolute : 'N/A'} coins`
        : `${modifierActionDisplay}${rule.modifier_value_percentage_rate !== null ? (parseFloat(rule.modifier_value_percentage_rate) * 100).toFixed(1) + '%' : 'N/A'}`;


    return (
        <Card className={cn(!rule.is_active && 'bg-muted/50 opacity-70')}> {/* [cite: 1, 16] */}
            <CardHeader>
                <div className="flex flex-col md:flex-row justify-between md:items-start gap-2">
                    <CardTitle className="text-base">{rule.rule_name}</CardTitle> {/* Shadcn CardTitle is h3 by default, adjusted for item context */}
                    <Badge variant={rule.is_active ? 'default' : 'outline'} className={cn( // Using outline for inactive to be less prominent than destructive [cite: 19]
                        "w-fit mt-1 md:mt-0",
                        rule.is_active ? 'bg-brand-growth-green hover:bg-brand-growth-green/90 text-white' : 'border-destructive text-destructive' // [cite: 1, 7]
                    )}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
                <p className="text-foreground"> {/* [cite: 1] */}
                    <strong>Modifier:</strong> {rule.modifier_type} - {modifierValueDisplay}
                </p>
                <p className="text-muted-foreground"> {/* [cite: 1] */}
                    <strong>Condition:</strong> {getConditionSummary(rule.condition_definition, rule.condition_config, questionList)}
                </p>
                {rule.description && <p className="text-xs text-muted-foreground italic mt-1">{rule.description}</p>}
            </CardContent>
            {canEdit && (
                <CardFooter className="flex flex-row justify-end gap-2 pt-4">
                    <Button variant="outline" size="sm" onClick={() => onEdit(rule)}>Edit</Button> {/* [cite: 12, 13] */}
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onDelete(rule.id)}
                    >
                        Delete
                    </Button> {/* [cite: 11, 13] */}
                </CardFooter>
            )}
        </Card>
    );
}